import React, { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import { useStore } from '../utils/StoreContext';
import { 
  Settings,
  LogOut,
  Store,
  Menu,
  ChevronDown,
  MapPin,
  Check,
  RefreshCw,
  AlertCircle,
  List,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Phone,
  Package
} from 'lucide-react';

const Layout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarMinimized, setSidebarMinimized] = useState(false);
  const [storeDropdownOpen, setStoreDropdownOpen] = useState(false);
  const storeDropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { 
    selectedStore, 
    currentStore, 
    stores, 
    loading, 
    error, 
    setSelectedStore, 
    refreshStores 
  } = useStore();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (storeDropdownRef.current && !storeDropdownRef.current.contains(event.target as Node)) {
        setStoreDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleStoreChange = (storeId: string) => {
    console.log('🏪 Store selection clicked:', storeId);
    setSelectedStore(storeId);
    setStoreDropdownOpen(false);
  };

  const handleRefreshStores = async () => {
    setStoreDropdownOpen(false);
    await refreshStores();
  };

  const navigation = [
    { name: 'Dashboard', href: '/', icon: BarChart3 },
    { name: 'Movement Recorder', href: '/movement-recorder', icon: Package },
    { name: 'Items', href: '/items', icon: List },
    { name: 'Settings', href: '/settings', icon: Settings },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const closeSidebar = () => setSidebarOpen(false);

  // Store Selector Component
  const StoreSelector: React.FC<{ isMobile?: boolean }> = ({ isMobile = false }) => (
    <div className="relative" ref={storeDropdownRef}>
      <button
        onClick={() => setStoreDropdownOpen(!storeDropdownOpen)}
        className={`flex items-center space-x-2 px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition-colors ${
          isMobile ? 'text-sm min-w-0 min-h-[44px] active:bg-gray-100' : 'text-sm'
        }`}
        style={isMobile ? { minHeight: '44px', touchAction: 'manipulation' } : {}}
      >
        <Store className="w-4 h-4 text-gray-500 flex-shrink-0" />
        <div className="text-left min-w-0 flex-1">
          <div className={`font-medium text-gray-900 truncate ${isMobile ? 'max-w-20' : ''}`}>
            {loading ? 'Loading...' : isMobile ? currentStore?.name.split(' ')[0] : currentStore?.name || 'No Store'}
          </div>
          {!isMobile && (
            <div className="text-xs text-gray-500 truncate">
              {loading ? 'Please wait...' : currentStore?.address || 'Select a store'}
            </div>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform flex-shrink-0 ${
          storeDropdownOpen ? 'rotate-180' : ''
        }`} />
      </button>

      {/* Dropdown */}
      {storeDropdownOpen && (
        <>
          {/* Backdrop for mobile */}
          {isMobile && (
            <div
              className="fixed inset-0 z-40 bg-black/20"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('🏪 Backdrop clicked, closing dropdown');
                setStoreDropdownOpen(false);
              }}
            />
          )}
          
          <div 
            className={`${isMobile ? 'fixed left-4 right-4 top-20' : 'absolute right-0 mt-1'} ${isMobile ? 'w-auto' : 'w-64'} z-[60] bg-white rounded-lg shadow-lg border border-gray-200 py-1`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('🏪 Dropdown container clicked');
            }}
          >
            <div className="px-3 py-2 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Select Store
                </div>
                {error && (
                  <div className="flex items-center space-x-1 text-xs text-amber-600">
                    <AlertCircle className="w-3 h-3" />
                    <span>Offline</span>
                  </div>
                )}
              </div>
            </div>
            
            {loading ? (
              <div className="px-3 py-4 text-center">
                <RefreshCw className="w-4 h-4 animate-spin mx-auto text-gray-400" />
                <div className="text-sm text-gray-500 mt-1">Loading stores...</div>
              </div>
            ) : (
              <>
                {stores.map((store) => (
                  <button
                    key={store.id}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('🏪 Store button clicked:', store.name);
                      handleStoreChange(store.id);
                    }}
                    onTouchEnd={(e) => {
                      if (isMobile) {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log('🏪 Store button touched:', store.name);
                        handleStoreChange(store.id);
                      }
                    }}
                    className={`w-full flex items-center justify-between px-3 py-2 text-left hover:bg-gray-50 transition-colors ${
                      isMobile ? 'min-h-[48px] active:bg-gray-100' : ''
                    }`}
                    style={isMobile ? { minHeight: '48px', touchAction: 'manipulation' } : {}}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-2 h-2 rounded-full ${
                        store.is_active ? 'bg-green-500' : 'bg-gray-300'
                      }`} />
                      <div>
                        <div className="font-medium text-gray-900">{store.name}</div>
                        <div className="text-sm text-gray-500">
                          {store.manager_name && `${store.manager_name} • `}{store.address}
                        </div>
                      </div>
                    </div>
                    {store.id === selectedStore && (
                      <Check className="w-4 h-4 text-primary-500" />
                    )}
                  </button>
                ))}
              </>
            )}
            
            <div className="border-t border-gray-100 mt-1 pt-1">
              <button 
                onClick={handleRefreshStores}
                className={`w-full px-3 py-2 text-left text-sm text-gray-600 hover:bg-gray-50 transition-colors ${
                  isMobile ? 'min-h-[44px] active:bg-gray-100' : ''
                }`}
                style={isMobile ? { minHeight: '44px', touchAction: 'manipulation' } : {}}
                disabled={loading}
              >
                <div className="flex items-center space-x-2">
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  <span>Refresh Stores</span>
                </div>
              </button>
              <button 
                className={`w-full px-3 py-2 text-left text-sm text-gray-600 hover:bg-gray-50 transition-colors ${
                  isMobile ? 'min-h-[44px] active:bg-gray-100' : ''
                }`}
                style={isMobile ? { minHeight: '44px', touchAction: 'manipulation' } : {}}
              >
                <div className="flex items-center space-x-2">
                  <MapPin className="w-4 h-4" />
                  <span>Manage Stores</span>
                </div>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 bg-white shadow-xl transform transition-all duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        ${sidebarMinimized ? 'w-16' : 'w-64'}
      `}>
        {/* Logo */}
        <div className="relative flex items-center justify-center h-20 bg-white px-4">
          <img 
            src="/images/btlogoback.png" 
            alt="Beautello Logo"
            className={`transition-all duration-300 w-auto object-contain ${
              sidebarMinimized ? 'h-8' : 'h-12'
            }`}
          />
          
          {/* Minimize Toggle Button - Desktop Only */}
          <button
            onClick={() => setSidebarMinimized(!sidebarMinimized)}
            className="hidden lg:flex absolute -right-3 top-1/2 transform -translate-y-1/2 w-6 h-6 bg-white border border-gray-200 rounded-full items-center justify-center shadow-md hover:shadow-lg transition-all duration-200 hover:bg-gray-50"
            title={sidebarMinimized ? 'Expand sidebar' : 'Minimize sidebar'}
          >
            {sidebarMinimized ? (
              <ChevronRight className="w-3 h-3 text-gray-600" />
            ) : (
              <ChevronLeft className="w-3 h-3 text-gray-600" />
            )}
          </button>
        </div>
        
        {/* Navigation */}
        <nav className={`mt-8 space-y-2 ${sidebarMinimized ? 'px-2' : 'px-4'}`}>
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <div key={item.name} className="relative group">
                <Link
                  to={item.href}
                  onClick={closeSidebar}
                  className={`nav-link ${isActive ? 'nav-link-active' : 'nav-link-inactive'} ${
                    sidebarMinimized ? 'justify-center px-3' : 'justify-start'
                  }`}
                  title={sidebarMinimized ? item.name : ''}
                >
                  <item.icon className={`w-5 h-5 ${sidebarMinimized ? '' : 'mr-3'}`} />
                  {!sidebarMinimized && item.name}
                </Link>
                
                {/* Tooltip for minimized mode */}
                {sidebarMinimized && (
                  <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-2 py-1 bg-gray-900 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                    {item.name}
                    <div className="absolute right-full top-1/2 transform -translate-y-1/2 border-4 border-transparent border-r-gray-900"></div>
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Logout button and version */}
        <div className={`absolute bottom-4 ${sidebarMinimized ? 'left-2 right-2' : 'left-4 right-4'}`}>
          <div className="space-y-3">
            <div className="relative group">
              <button 
                onClick={handleLogout}
                className={`nav-link nav-link-inactive w-full ${
                  sidebarMinimized ? 'justify-center px-3' : 'justify-start'
                }`}
                title={sidebarMinimized ? 'Logout' : ''}
              >
                <LogOut className={`w-5 h-5 ${sidebarMinimized ? '' : 'mr-3'}`} />
                {!sidebarMinimized && 'Logout'}
              </button>
              
              {/* Tooltip for minimized mode */}
              {sidebarMinimized && (
                <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-2 py-1 bg-gray-900 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                  Logout
                  <div className="absolute right-full top-1/2 transform -translate-y-1/2 border-4 border-transparent border-r-gray-900"></div>
                </div>
              )}
            </div>
            
            {/* Version number */}
            <div className={`text-center ${sidebarMinimized ? 'px-1' : 'px-2'}`}>
              <span className="text-xs text-gray-400 font-mono">
                {sidebarMinimized ? 'v0.1.6' : 'Version 0.1.6'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile header */}
        <header className="bg-white shadow-sm border-b border-gray-200 lg:hidden">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <Menu className="w-6 h-6" />
              </button>
              <img 
                src="/images/btlogoback.png" 
                alt="Beautello Logo"
                className="h-7 w-auto object-contain"
              />
            </div>
            <div className="flex items-center space-x-2">
              <StoreSelector isMobile={true} />
              <div className="flex flex-col items-end">
                <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    {user?.name?.charAt(0) || 'A'}
                  </span>
                </div>
                <div className="flex items-center space-x-1 mt-1">
                  <Phone className="w-2 h-2 text-gray-400" />
                  <span className="text-xs text-gray-500">
                    {user?.phone || 'Admin'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Desktop header */}
        <header className="hidden lg:block bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between px-6 py-4">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">
                {navigation.find(item => item.href === location.pathname)?.name || 'Dashboard'}
              </h2>
              <p className="text-gray-500 mt-1">
                Stock Management System • {currentStore?.name}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <StoreSelector />
              <div className="text-right">
                <div className="flex items-center justify-end space-x-1">
                  <Phone className="w-3 h-3 text-gray-400" />
                  <p className="text-sm font-medium text-gray-900">
                    {user?.phone || 'Admin User'}
                  </p>
                </div>
                <p className="text-xs text-gray-500">
                  {user?.user_type || 'Administrator'}
                </p>
              </div>
              <div className="w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-medium">
                  {user?.name?.charAt(0) || 'A'}
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout; 